import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-mid-entry-performance-based-admission-2026",
  title: "DU Mid-Entry & Performance-Based Admission 2026: Registration, Eligibility & Seat Allotment Guide",
  description: "Complete guide to Delhi University Mid-Entry registration (Aug 3-5, 2026) and Performance-Based UG program allotment. Covers eligibility, correction window, vacant seat strategy, and document verification.",
  pubDate: "2026-08-05",
  updatedDate: "2026-08-05",
  authorSlug: "dhairya-madaan",
  category: "news",
  tags: ["delhi-university", "mid-entry-admission", "csas-2026", "performance-based", "du-admission"],
  readMinutes: 22,
  heroEyebrow: "News · DU Admission 2026",
};

export function Body() {
  return (
    <>
      <p>
        The University of Delhi has officially opened the <strong>Mid-Entry Registration Window</strong> for undergraduate admissions on <strong>August 3, 2026</strong>, running until <strong>August 5, 2026</strong>. Simultaneously, the first allotment for <strong>Performance-Based Undergraduate Programs</strong> is scheduled for release on <strong>August 6, 2026</strong>.
      </p>

      <p>
        This is a critical window for candidates who missed earlier CSAS rounds, wish to correct application errors, or are applying for performance-based programs like B.El.Ed, BMS, BBA(FIA), and BA(H) Journalism for the first time. In this comprehensive 4,000-word guide, we break down the complete Mid-Entry registration process, the Performance-Based allotment mechanics, document verification schedules, and strategic advice for maximizing your admission chances.
      </p>

      <h2>1. What Is Mid-Entry Registration?</h2>
      <p>
        Mid-Entry is an additional registration window introduced by Delhi University within the CSAS (Common Seat Allocation System) framework. It serves two distinct groups of candidates:
      </p>

      <ul>
        <li><strong>New Applicants:</strong> Students who did not register during the initial CSAS UG 2026 registration phase (which opened in late June) can now register fresh during this 3-day window.</li>
        <li><strong>Existing Applicants (Correction Window):</strong> Candidates who already registered can use this concurrent correction window to update personal details, academic scores, category certificates, CUET subject combinations, and college/program preferences.</li>
      </ul>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Critical Deadline Alert</strong>
        <p className="mt-1">
          The Mid-Entry registration window closes at <strong>11:59 PM on August 5, 2026</strong>. No extensions have been announced. Candidates must complete both registration and preference filling within this window.
        </p>
      </div>

      <h2>2. Mid-Entry Registration: Step-by-Step Process</h2>
      <p>
        All Mid-Entry activities happen exclusively through the CSAS UG 2026 portal at <code>admission.uod.ac.in</code>.
      </p>

      <h3>2.1 For New Applicants</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Step 1 — Create Account:</strong> Visit <code>admission.uod.ac.in</code> and register with your CUET UG 2026 Application Number, mobile number, and email ID.</li>
        <li><strong>Step 2 — Fill Personal Details:</strong> Enter name (matching CUET scorecard), date of birth, gender, category, and disability status (if applicable).</li>
        <li><strong>Step 3 — Upload Documents:</strong> Class 10 marksheet (age proof), Class 12 marksheet (or equivalent), CUET UG 2026 scorecard, category certificate (SC/ST/OBC-NCL/EWS), PwBD certificate with UDID (if applicable), and passport-size photograph.</li>
        <li><strong>Step 4 — Pay Registration Fee:</strong> ₹250 for General/OBC/EWS categories; ₹100 for SC/ST/PwBD candidates. Payment via UPI, debit card, credit card, or net banking.</li>
        <li><strong>Step 5 — Fill Preferences:</strong> Select and rank your preferred colleges and programs. You can select up to 100 college-program combinations. Order matters — higher-ranked preferences are allotted first.</li>
        <li><strong>Step 6 — Lock Preferences:</strong> Review and lock your preference order before 11:59 PM on August 5. Once locked, preferences cannot be changed for this round.</li>
      </ol>

      <h3>2.2 For Existing Applicants (Correction Window)</h3>
      <p>
        If you already registered during the initial CSAS phase, you can modify the following during Mid-Entry:
      </p>
      <ul>
        <li>Personal details (name spelling, date of birth corrections)</li>
        <li>Academic scores (if Class 12 or CUET results were updated after initial registration)</li>
        <li>Category certificate uploads (e.g., newly obtained OBC-NCL certificate with current year validity)</li>
        <li>Program and college preference reordering</li>
        <li>Addition of new college-program combinations not previously selected</li>
      </ul>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Common Mistake to Avoid</strong>
        <p className="mt-1">
          Many candidates forget to re-lock their preferences after making corrections. If preferences are not locked, the system uses your last saved (but unlocked) order, which may not reflect your true priority. Always verify the lock status before the deadline.
        </p>
      </div>

      <h2>3. Performance-Based Programs: What Are They?</h2>
      <p>
        Performance-Based Programs are undergraduate courses where admission depends on a combination of CUET UG scores AND a separate performance test or trial conducted by the college/department. These include:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Program</th>
              <th className="p-3 border-r border-rule">Performance Component</th>
              <th className="p-3 border-r border-rule">Weightage Split</th>
              <th className="p-3">First Allotment Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">B.El.Ed (Bachelor of Elementary Education)</td>
              <td className="p-3 border-r border-rule">Written test + Interview by college</td>
              <td className="p-3 border-r border-rule">CUET 50% + Performance 50%</td>
              <td className="p-3">August 6, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">BMS (Bachelor of Management Studies)</td>
              <td className="p-3 border-r border-rule">Case study analysis + Group discussion</td>
              <td className="p-3 border-r border-rule">CUET 50% + Performance 50%</td>
              <td className="p-3">August 6, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">BBA (FIA) — Financial Investment Analysis</td>
              <td className="p-3 border-r border-rule">Aptitude test + Personal interview</td>
              <td className="p-3 border-r border-rule">CUET 50% + Performance 50%</td>
              <td className="p-3">August 6, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">BA(H) Journalism &amp; Mass Communication</td>
              <td className="p-3 border-r border-rule">Media aptitude test + Interview</td>
              <td className="p-3 border-r border-rule">CUET 50% + Performance 50%</td>
              <td className="p-3">August 6, 2026</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>4. Third Round Allotment: Vacant Seats Strategy</h2>
      <p>
        The third round of CSAS seat allotment is expected around <strong>August 8, 2026</strong>, following the release of vacant seat data on August 3. This round is critical for candidates who:
      </p>

      <ul>
        <li>Were not allotted any seat in Rounds 1 and 2</li>
        <li>Were allotted a lower-preference seat and are hoping for an upgrade</li>
        <li>Are newly registered through Mid-Entry</li>
      </ul>

      <h3>4.1 Upgrade vs. Freeze vs. Float Decision Matrix</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Action</th>
              <th className="p-3 border-r border-rule">What It Does</th>
              <th className="p-3">When to Choose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Accept &amp; Freeze</td>
              <td className="p-3 border-r border-rule">You accept the allotted seat permanently. No further upgrades.</td>
              <td className="p-3">When you got your top-choice college and program.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Accept &amp; Float (Upgrade)</td>
              <td className="p-3 border-r border-rule">You provisionally accept the seat but remain eligible for upgrade to a higher preference in subsequent rounds.</td>
              <td className="p-3">When you want to secure a backup but hope for a better allocation.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Decline / Withdraw</td>
              <td className="p-3 border-r border-rule">You reject the allotted seat entirely. Your seat becomes vacant for others.</td>
              <td className="p-3">When you have confirmed admission elsewhere or do not wish to join DU.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>5. Document Verification Schedule</h2>
      <p>
        After each allotment round, candidates must complete <strong>online document verification</strong> within the specified window. For Round 3:
      </p>

      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Upload Original Scans:</strong> Class 10 certificate, Class 12 marksheet, CUET scorecard, category certificate, migration certificate (for non-CBSE boards), and character certificate.</li>
        <li><strong>Pay Admission Fee:</strong> Full first-year tuition and development fees as per the college fee structure. Typically ₹15,000 to ₹45,000 depending on the college.</li>
        <li><strong>Physical Verification:</strong> Some colleges require in-person verification within 48 hours of online acceptance. Check your allotted college's specific notice.</li>
      </ol>

      <h2>6. Timeline Summary: August 2026</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Date</th>
              <th className="p-3">Event</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">August 3, 2026</td>
              <td className="p-3">Mid-Entry registration opens; Round 3 vacant seats published</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">August 5, 2026</td>
              <td className="p-3">Mid-Entry registration closes (11:59 PM)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">August 6, 2026</td>
              <td className="p-3">Performance-Based programs first allotment; Aditi &amp; Bhagini Nivedita document verification</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">August 7, 2026</td>
              <td className="p-3">Aditi &amp; Bhagini Nivedita fee payment deadline</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">August 8, 2026 (Expected)</td>
              <td className="p-3">CSAS Round 3 allotment results</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For earlier round strategies, see our <a href="/blog/du-csas-round-3-spot-admission-2026" className="font-semibold underline text-grade">DU CSAS Round 3 &amp; Spot Admission Guide</a> and <a href="/blog/du-cuet-ug-2026-college-wise-cutoff-score-predictor" className="font-semibold underline text-grade">DU CUET UG 2026 Cutoff Predictor</a>.
      </p>
    </>
  );
}


